import {Layout} from "./Layout";
import {useEffect, useState} from "react";
import {
    signIn,
    signOut,
    sendUserAttributeVerificationCode,
    confirmUserAttribute,
    resetPassword,
    confirmResetPassword,
    confirmSignIn as amplifyConfirmSignIn,
    fetchAuthSession
} from 'aws-amplify/auth';
import {NavLink, useHistory} from "react-router-dom";
import {I18n} from "aws-amplify/utils";
import {InputText} from "../form/InputText";
import {InputType} from "../form/InputType";
import Bus from "../../js/Bus";
import {showError, showMessage} from "../../js/main";


/**
 * @global
 * @typedef {Object} Pace
 * @property {Function} start
 * @property {Function} stop
 * @property {Function} restart
 * @property {Function} track
 */

/* global Pace */

export const sanitizeUsername = (username) => {
return username.normalize("NFKC")                 // normalize unicode
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // remove zero-width chars
    .trim();
}


export const Login = () => {

    const [maskState, setMaskState] = useState("login");

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");

    // verify
    const [verifyMailCode, setVerifyMailCode] = useState("");

    // renew password
    const [newPassword, setNewPassword] = useState("");
    const [newPassword2, setNewPassword2] = useState("");

    const history = useHistory();

    useEffect(() => {
        I18n.setLanguage("de");

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const login = async () => {
        Pace.restart();
        return Pace.track(async () => {
            try {
                let sanitizedUsername = sanitizeUsername(username);
                setUsername(sanitizedUsername);
                const result = await signIn({username: sanitizedUsername, password});

                if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    setMaskState('confirmSignIn');
                    return;
                }

                const session = await fetchAuthSession({ forceRefresh: true });

                if (!session.tokens.idToken.payload["email_verified"]) {
                    await sendUserAttributeVerificationCode({
                        userAttributeKey: 'email',
                    });
                    setMaskState('confirmEmail');
                    return;
                }

                Bus.emit('loginstate', 'signedIn');

            } catch (error) {
                if (error.name === 'UserAlreadyAuthenticatedException') {
                    signOut().then(login);
                    return;
                }
                showError(I18n.get(error.message, error.message))
            }
        });
    }

    const confirmSignIn = async () => {
        Pace.restart();
        return Pace.track(async () => {
            try {
                if (newPassword !== newPassword2) {
                    showError("Passwörter stimmen nicht überein");
                    return;
                }
                await amplifyConfirmSignIn({
                    challengeResponse: newPassword
                });

                showMessage("Password erfolgreich geändert, bitte loggen Sie sich nochmals ein.")

                signOut().then(() => setMaskState('login'));
            } catch (error) {
                showError(I18n.get(error.message, error.message))
            }
        });
    }

    const confirmEmail = async () => {
        Pace.restart();
        return Pace.track(async () => {
            try {
                await confirmUserAttribute({
                    userAttributeKey: 'email',
                    confirmationCode: verifyMailCode,
                });

                showMessage("E-Mail Adresse bestätigt. Bitte loggen Sie sich nochmals ein.")

                signOut().then(() => setMaskState('login'));

            } catch (error) {
                showError(I18n.get(error.message, error.message))
            }
        });
    }

    const forgotPw = async () => {
        Pace.restart();
        return Pace.track(async () => {
            try {
                await resetPassword({username: sanitizeUsername(username)});

                setMaskState('resetPw');
            } catch (error) {
                showError(I18n.get(error.message, error.message))
            }
        });
    }

    const resetPw = async () => {
        Pace.restart();
        return Pace.track(async () => {
            try {
                if(newPassword !== newPassword2) {
                    showError("Passwörter stimmen nicht überein");
                    return;
                }

                await confirmResetPassword({ username: sanitizeUsername(username) , confirmationCode: verifyMailCode, newPassword});

                showMessage("Passwort erfolgreich geändert. Bitte melden Sie sich erneut an.");

                setMaskState('login');
            } catch (error) {
                showError(I18n.get(error.message, error.message))
            }
        });
    }

    return (
        <Layout username={null}>
            <div className="default">
                <div className="guest-request-tile">
                    <div className="guest-request-content">

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            switch (maskState) {
                                case "login":
                                      login();
                                      break;
                                case "confirmEmail":
                                    confirmEmail();
                                    break;
                                case "confirmSignIn":
                                    confirmSignIn();
                                    break;
                                case "forgotPw":
                                    forgotPw();
                                    break;
                                case "resetPw":
                                    resetPw();
                                    break;
                            }
                        }}>
                            {maskState === 'login' && <>
                                <div className="guest-request-form-header">
                                    <h3 className="header">Anmelden</h3>
                                </div>
                                <div className="guest-request-description mb-2">
                                    <InputText name={"username"} label={"Username"} value={username}
                                               setter={setUsername} required={true}/>
                                    <InputType name={"password"} label={"Passwort"} value={password}
                                               setter={setPassword} required={true} type={'password'}/>
                                    <small>Passwort vergessen? <a onClick={() => setMaskState('forgotPw')}>Hier zurücksetzen</a></small>

                                </div>
                                <div className="guest-request-footer">
                                    <button type="submit" className={"primary-button"}>Anmelden</button>
                                </div>
                            </>}
                            {maskState === 'confirmEmail' && <>
                                <div className="guest-request-form-header">
                                    <h3 className="header">E-Mail bestätigen</h3>
                                </div>
                                <div className="guest-request-description mb-2">
                                    <p>Sie haben eine Mail mit einem Verifizierungscode erhalten. Geben Sie diesen bitte ein.</p>
                                    <InputText name={"verify-mail-code"} label={"Code"} value={verifyMailCode}
                                               setter={setVerifyMailCode} required/>
                                </div>
                                <div className="guest-request-footer">
                                    <button type="submit" className={"primary-button"}>Code bestätigen
                                    </button>
                                </div>
                            </>}
                            {maskState === 'forgotPw' && <>
                                <div className="guest-request-form-header">
                                    <h3 className="header">Passwort vergessen</h3>
                                </div>
                                <div className="guest-request-description mb-2">
                                    <InputText name={"username"} label={"Username"} value={username}
                                               setter={setUsername} required={true} />
                                </div>
                                <div className="guest-request-footer">
                                    <button type="submit" className={"primary-button"}>Passwort zurücksetzen.
                                    </button>
                                </div>
                            </>}
                            {(maskState === 'resetPw' || maskState === 'confirmSignIn') && <>
                                <div className="guest-request-form-header">
                                    <h3 className="header">Neues Passwort vergeben</h3>
                                </div>
                                <div className="guest-request-description mb-2">
                                    {maskState === 'resetPw' && <>
                                        <p>Sie haben eine Mail mit einem Verifizierungscode erhalten. Geben Sie diesen bitte ein.</p>
                                        <InputText name={"verify-mail-code"} label={"Code"} value={verifyMailCode}
                                                   setter={setVerifyMailCode} required/>
                                    </>}
                                    <InputType name={"newpassword"} label={"Neues Passwort"} value={newPassword}
                                               setter={setNewPassword} required={true} type={'password'}/>
                                    <InputType name={"newpassword2"} label={"Neues Passwort wiederholen"} value={newPassword2}
                                               setter={setNewPassword2} required={true} type={'password'}/>
                                </div>
                                <div className="guest-request-footer">
                                    <button type="submit" className={"primary-button"}>Passwort aktualisieren
                                    </button>
                                </div>
                            </>}
                        </form>
                    </div>
                </div>
                <div className="guest-request-tile">
                    <div className="guest-request-content">
                        <div className="guest-request-form-header">
                            <h3 className="header">Schlüssel nachmachen</h3>
                        </div>
                        <div className="guest-request-description mb-2">
                            <p>Hier können Sie als nicht registrierter Kunde eine Schlüsselbestellung
                                aufgeben.<br/><br/>
                                Bestellen Sie schnell und unkompliziert einen Ersatzschlüssel. Dazu müssen Sie einfach
                                nur zwei Fotos machen, um den Rest kümmern wir uns.
                            </p>
                        </div>
                        <div className="guest-request-footer">
                            <NavLink to="guest-request">
                                <button type="submit" className={"primary-button"}>Schlüssel bestellen</button>
                            </NavLink>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );

};

